/**
 * Dispatcher: maps a ConditionNode's type to the correct renderer component.
 * Matches ~/code/ui condition-editor-v2 render-node.gts
 */

import type { ConditionNode as ConditionNodeType } from '../types';
import { ConditionGroup } from './condition-group';
import { AttributeCondition } from './renderers/attribute';
import { EventCondition } from './renderers/event';
import { DeviceCondition } from './renderers/device';
import { PageviewCondition } from './renderers/pageview';
import { ScreenviewCondition } from './renderers/screenview';
import { FormCondition } from './renderers/form';
import { InboundCondition } from './renderers/inbound';
import { OptOutCondition } from './renderers/opt-out';
import { MessageCondition } from './renderers/message';
import { SegmentCondition } from './renderers/segment';
import { RelationshipCondition } from './renderers/relationship';
import { UnknownCondition } from './renderers/unknown';

interface ConditionNodeProps {
  node: ConditionNodeType;
  depth?: number;
  workspaceId: number;
}

export function ConditionNode({ node, depth = 0, workspaceId }: ConditionNodeProps) {
  switch (node.type) {
    case 'group':
      return <ConditionGroup node={node} depth={depth} workspaceId={workspaceId} />;
    case 'attribute':
      return <AttributeCondition node={node} />;
    case 'event':
      return <EventCondition node={node} />;
    case 'segment':
      return <SegmentCondition node={node} workspaceId={workspaceId} />;
    case 'device':
      return <DeviceCondition node={node} />;
    case 'pageview':
      return <PageviewCondition node={node} />;
    case 'screenview':
      return <ScreenviewCondition node={node} />;
    case 'form':
      return <FormCondition node={node} />;
    case 'message':
      return <MessageCondition node={node} workspaceId={workspaceId} />;
    case 'inbound':
      return <InboundCondition node={node} />;
    case 'opt_out':
      return <OptOutCondition node={node} />;
    case 'relationship':
      return <RelationshipCondition node={node} />;
    case 'unknown':
      return <UnknownCondition node={node} />;
    default: {
      const _exhaustive: never = node;
      return <UnknownCondition node={{ type: 'unknown', raw: _exhaustive as Record<string, unknown> }} />;
    }
  }
}
